import React from "react";
import Modal from "react-modal";

import { useAppDispatch } from "../../../app/hooks";
import { DumpEvent } from "../../../models";
import { createWeightOverageInvoice } from "../../Orders/ordersSlice";

// Reuse the approve-overage modal styling so this looks identical.
import "../ApproveOverageModal/ApproveOverageModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
    maxWidth: "460px",
    width: "90%",
    padding: "0",
  },
};

const FEE_PER_POUND = 0.045; // display estimate; backend uses the pricing setting

interface Props {
  // The dump receipt to raise an overage for; null = modal closed.
  event: DumpEvent | null;
  onClose: () => void;
  onInvoiced: () => void;
}

// Included-weight allowance (lb) by dumpster size — mirrors the backend
// pricing default (15yd → 6000, else → 4000).
function allowanceForSize(size: string): number {
  return size === "15" ? 6000 : 4000;
}

function calcTotal(dumped: number, allowed: number): string {
  const overage = Math.floor(dumped - allowed);
  if (overage <= 0) return "$0.00";
  return `$${(overage * FEE_PER_POUND).toFixed(2)}`;
}

export function DumpReceiptOverageModal({ event, onClose, onInvoiced }: Props) {
  const dispatch = useAppDispatch();
  const [dumped, setDumped] = React.useState(0);
  const [allowed, setAllowed] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Prefill from the dump receipt each time the modal opens: the receipt's
  // weight is tons → convert to pounds; allowance from the order's size.
  React.useEffect(() => {
    if (!event) return;
    setDumped(event.weight != null ? Math.round(event.weight * 2000) : 0);
    setAllowed(allowanceForSize(event.orderDumpsterSize));
    setError(null);
  }, [event]);

  const canInvoice = !!event && !!event.orderId;
  const createDisabled = saving || !canInvoice || dumped <= allowed;

  const handleCreate = async () => {
    if (!event || !event.orderId) return;
    setSaving(true);
    setError(null);
    try {
      await dispatch(
        createWeightOverageInvoice({
          orderId: event.orderId,
          form: {
            allowedWeight: String(allowed),
            dumpedWeight: String(dumped),
          },
        })
      ).unwrap();
      onInvoiced();
      onClose();
    } catch {
      setError("Could not create the invoice. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={event !== null}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="Weight overage invoice"
    >
      <div className="approve-overage-modal">
        <div className="approve-overage-header">
          <h3>Weight overage invoice</h3>
          <button
            className="approve-overage-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="approve-overage-body">
          {event && (
            <div className="approve-overage-order">
              <span className="order-name">
                {event.orderName || "(unattributed)"}
              </span>
              <span className="order-address">{event.orderAddress}</span>
            </div>
          )}
          {!canInvoice && (
            <div className="overage-error">
              This dump isn’t attributed to an order, so an overage invoice
              can’t be raised.
            </div>
          )}
          <label className="overage-field">
            <span>Dumped weight (lb)</span>
            <input
              type="number"
              min="0"
              step="1"
              value={dumped}
              onChange={(e) => setDumped(Number(e.target.value))}
            />
          </label>
          <label className="overage-field">
            <span>Allowed weight (lb)</span>
            <input
              type="number"
              min="0"
              step="1"
              value={allowed}
              onChange={(e) => setAllowed(Number(e.target.value))}
            />
          </label>
          <div className="overage-total">
            Overage total: <strong>{calcTotal(dumped, allowed)}</strong>
          </div>
          {error && <div className="overage-error">{error}</div>}
        </div>
        <div className="approve-overage-footer">
          <button className="btn btn-reject" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-approve"
            onClick={handleCreate}
            disabled={createDisabled}
          >
            {saving ? "Saving…" : "Create invoice"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
